import { PROTECTED_PERMISSIONS } from './permissions';

/**
 * Role-based guide. The guide is ONE catalog of topics (texts in apps/web/src/pages/help-*.ts, written once per
 * language); every role tab is a filtered view of it. This list is the single place that says which topic is
 * shown in which tab, so a new feature gets its guide text once and is tagged once.
 *
 * employee  everyone: booking, week, absences, own figures
 * external  external partners: booking on the assigned projects and tasks, the week, what they see and what not
 * lead      project lead: stage-1 approval, plan hours, analysis and Forma settings of the own projects
 * office    office and management (one role): approvals stage 2, people, billing, customers, month closing, announcements
 * admin     technical administration: accounts, rights, settings, Forma synchronisation, backup, background jobs
 */
export const GUIDE_AUDIENCES = ['employee', 'external', 'lead', 'office', 'admin'] as const;
export type GuideAudience = (typeof GUIDE_AUDIENCES)[number];

const ALL = GUIDE_AUDIENCES;

/** In reading order. The numbering of a tab counts the topics it shows. */
export const GUIDE_TOPICS = [
  { id: 'home', audiences: ALL },
  { id: 'glance', audiences: ALL },
  { id: 'external', audiences: ['external'] },
  { id: 'record', audiences: ['employee', 'external'] },
  { id: 'quick', audiences: ['employee', 'external'] },
  { id: 'routine', audiences: ['employee', 'external'] },
  { id: 'where', audiences: ['employee'] },
  { id: 'path', audiences: ['employee', 'external'] },
  { id: 'rhythm', audiences: ['employee'] },
  { id: 'absence', audiences: ['employee'] },
  { id: 'leavedays', audiences: ['employee'] },
  { id: 'signals', audiences: ['employee'] },
  { id: 'planactual', audiences: ['employee'] },
  { id: 'symbols', audiences: ['employee', 'external'] },
  { id: 'help', audiences: ['employee', 'external'] },
  { id: 'roles', audiences: ['lead', 'office', 'admin'] },
  { id: 'approvals', audiences: ['lead', 'office'] },
  { id: 'projectrules', audiences: ['lead', 'office'] },
  { id: 'hr', audiences: ['office'] },
  { id: 'hrtools', audiences: ['office'] },
  { id: 'people', audiences: ['office', 'admin'] },
  { id: 'protection', audiences: ['office', 'admin'] },
  { id: 'planning', audiences: ['lead', 'office'] },
  { id: 'review', audiences: ['office'] },
  { id: 'analysis', audiences: ['lead', 'office'] },
  { id: 'staffing', audiences: ['lead', 'office'] },
  { id: 'billing', audiences: ['office'] },
  { id: 'customers', audiences: ['office'] },
  { id: 'closing', audiences: ['office'] },
  { id: 'publish', audiences: ['office'] },
  { id: 'forma', audiences: ['lead', 'office', 'admin'] },
  { id: 'settings', audiences: ['admin'] },
  { id: 'automation', audiences: ['admin'] },
  { id: 'reports', audiences: ['admin'] },
  // round 5, request 37: IT requests go through Teams for now. Remove this topic (and its texts in help-topics.ts) when
  // Projektzeit gets a module for IT requests.
  { id: 'itnote', audiences: ['employee', 'lead', 'office', 'admin'] },
] as const satisfies readonly { id: string; audiences: readonly GuideAudience[] }[];

export type GuideTopicId = (typeof GUIDE_TOPICS)[number]['id'];

/** Rights for accounts, settings, Forma and backup: what a technical administrator holds. */
const TECHNICAL_RIGHTS = ['users.manage', 'rights.manage', 'settings.manage', 'sync.manage', 'backup.manage'] as const;

/**
 * The tab that opens first: the one that matches what the person does in the system. It follows the rights, not the
 * name of the role, because single rights can be granted (an assistant with approvals belongs to the office tab).
 * Business rights (approvals, billing, HR, figures, customers, announcements) come first: someone who holds them and
 * also administers accounts is mainly an office or management person. Then technical rights, then leading a project.
 */
export function defaultGuideAudience(permissions: Iterable<string>, leadsProject: boolean, kind: 'employee' | 'external' = 'employee'): GuideAudience {
  const held = new Set(permissions);
  if (PROTECTED_PERMISSIONS.some((p) => held.has(p))) return 'office';
  if (TECHNICAL_RIGHTS.some((p) => held.has(p))) return 'admin';
  if (leadsProject) return 'lead';
  return kind === 'external' ? 'external' : 'employee';
}

/**
 * Which guides a person may open (request 30 of round 4), by the role hierarchy external < employee < project lead <
 * office and management; the technical administration sees every guide like the office. Externals see only their own
 * guide (the others describe how the company works inside), employees only theirs, project leads also the guides
 * below them. The own guide is always first.
 */
export function visibleGuideAudiences(own: GuideAudience): GuideAudience[] {
  switch (own) {
    case 'external':
      return ['external'];
    case 'employee':
      return ['employee'];
    case 'lead':
      return ['lead', 'employee', 'external'];
    default:
      return [own, ...GUIDE_AUDIENCES.filter((a) => a !== own)];
  }
}

/** `?guide=` of a link or of the printed PDFs; `team` is the old name of the employee guide. */
export function parseGuideParam(value: string | null | undefined): GuideAudience | undefined {
  if (value === 'team') return 'employee';
  return GUIDE_AUDIENCES.find((a) => a === value);
}

/** The topics of one tab, in reading order. */
export function guideTopics(audience: GuideAudience): GuideTopicId[] {
  return GUIDE_TOPICS.filter((t) => (t.audiences as readonly GuideAudience[]).includes(audience)).map((t) => t.id);
}
